import React, { useState, useEffect, useRef } from "react";
import i18n, { loadTenantTranslations } from "./i18n";
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation, useNavigationType, useNavigate, useLoaderData } from "react-router-dom";
import { HospitalProvider, useHospital } from "./components/HospitalContext";
import "./stylings/fonts.css";
import Login from "./flow/login/Login";
import ForgotPassword from "./screens/ForgotPassword";
import ResetPassword from "./screens/ResetPassword";
import ErrorElement from "./screens/ErrorElement";
import NotFound from "./screens/NotFound";

import MayoFAQ from "./tenants/mayo/patientFacing/MayoFAQ";
import MayoLanding1 from "./tenants/mayo/patientFacing/mayo1/MayoLanding1";
import MayoLanding3 from "./tenants/mayo/patientFacing/mayo4/MayoLanding3";
import MayoChooseAvatar from "./tenants/mayo/patientFacing/mayo1/MayoChooseAvatar";
import MayoVideoPage1 from "./tenants/mayo/patientFacing/mayo1/MayoVideoPage1";
import MayoVideoPage3 from "./tenants/mayo/patientFacing/mayo4/MayoVideoPage3";
import PatientShuttle from "./tenants/mayo/patientFacing/mayo1/PatientShuttle";
import TermsOfUse from "./tenants/mayo/patientFacing/TermsOfUse";
import MayoLanding2TAVR from "./tenants/mayo/patientFacing/mayo3/MayoLanding2TAVR";
import MayoVideoPage2TAVR from "./tenants/mayo/patientFacing/mayo3/MayoVideoPage2TAVR";
import MayoLanding2Angiogram from "./tenants/mayo/patientFacing/mayo2/MayoLanding2Angiogram";
import MayoVideoPage2Angiogram from "./tenants/mayo/patientFacing/mayo2/MayoVideoPage2Angiogram";
import DemoPatientLanding from "./demo/patient/DemoPatientLanding";
import DemoChooseAvatar from "./demo/patient/DemoPatientChooseAvatar";
import DemoPatientVideo from "./demo/patient/DemoPatientVideo";
import DemoPatientShuttle from "./demo/patient/DemoPatientShuttle";
import { ToastContainer } from "react-toastify";
import HospitalSelection from "./flow/login/HospitalSelection";
import Panel from "./flow/panel/panel";
import axios, { HttpStatusCode } from "axios";
import HospitalApi from "./api/hospital";
import Gister from "./flow/gister/Gister";
import PatientFacing from "./flow/patientFacing/PatientFacing";
import GILanding from "./tenants/gi/patientFacing/GILanding";
import GILegal from "./tenants/gi/patientFacing/TOS/GILegal";
import AdminPage from "./pages/AdminPage";
import FacilityDetailPage from "./pages/FacilityDetailPage";
import CSVUploadPage from "./pages/CSVUploadPage";
import AdminStatisticsPage from "./pages/AdminStatisticsPage";
import AdminVideoTestPage from "./pages/AdminVideoTestPage";
import AdminDoctorsPage from "./pages/AdminDoctorsPage";
import AdminLaxativesPage from "./pages/AdminLaxativesPage";
import AdminGuard from "./components/AdminGuard";
import AdminLayout from "./components/AdminLayout";
import GICharacterSelection from "./tenants/gi/patientFacing/GICharacterSelection";
import GIQuestionnaire from "./tenants/gi/patientFacing/GIQuestionnaire";
import QuestionnaireProvider from "./tenants/gi/patientFacing/QuestionnaireProvider";
import GIVideo from "./tenants/gi/patientFacing/GIVideo";
import GI2Landing from "./tenants/gi/patientFacing/gi2/GI2Landing";
import GI2VideoPage1 from "./tenants/gi/patientFacing/gi2/GI2VideoPage1";
import { LanguageProvider } from "./providers/LanguageProvider";
import { HospitalLayout, hospitalLoader } from "./providers/HospitalProvider";

function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType(); // "PUSH", "POP", or "REPLACE"

  useEffect(() => {
    if (navigationType === "PUSH") {
      // only scroll to top on new navigation
      window.scrollTo(0, 0);
    }
  }, [pathname, navigationType]);

  return <Outlet />;
}


const router = createBrowserRouter([
  {
    loader: hospitalLoader,
    element: 
      <HospitalLayout>
        <LanguageProvider>
            <Outlet />
        </LanguageProvider>
      </HospitalLayout>,
    errorElement: <ErrorElement />,
    children: [
      // ---- Public Links - don't need to load hospital ---- 
      {
        children: [
          { path: "Login", element: <HospitalSelection /> },
          { path: "*", element: <NotFound /> },

          {
            path: "patient",
            children: [
              { path: "start/:eventId", element: <PatientFacing /> },

              // --- MAYO ---
              { path: "mayo/terms", element: <TermsOfUse /> },

              { path: "mayo/landing1/:eventId", element: <MayoLanding1/> },
              { path: "mayo/choose-avatar/:eventId", element: <MayoChooseAvatar /> },
              { path: "mayo/video_page1/:eventId", element: <MayoVideoPage1 /> },
              { path: "mayo/shuttle/", element: <PatientShuttle /> },
              
              { path: "mayo/landing2_angiogram/:eventId", element: <MayoLanding2Angiogram /> },
              { path: "mayo/video_page2_angiogram/:eventId", element: <MayoVideoPage2Angiogram /> },

              { path: "mayo/landing2_tavr/:eventId", element: <MayoLanding2TAVR/> },
              { path: "mayo/video_page2_tavr/:eventId", element: <MayoVideoPage2TAVR /> },

              { path: "mayo/faq", element: <MayoFAQ />},

              { path: "mayo/landing3/:eventId", element: <MayoLanding3/> },
              { path: "mayo/video_page3/:eventId", element: <MayoVideoPage3 /> },

              // --- GI ---
              { path: "gi/landing/:eventId", element: <GILanding /> },
              { path: "gi/legal", element: <GILegal />},
              { path: "gi/choose-avatar", element: <GICharacterSelection />},
              {
                path: "gi/Questionnaire", 
                element: <QuestionnaireProvider />,
                children: [{ path: ":questionKey", element: <GIQuestionnaire /> }],
              },
              { path: "gi/video", element: <GIVideo />},

              // --- GI2 (2-Day Prep) ---
              { path: "gi2/landing/:eventId", element: <GI2Landing /> },
              { path: "gi2/choose-avatar", element: <GICharacterSelection /> },
              {
                path: "gi2/Questionnaire",
                element: (
                  <QuestionnaireProvider
                    redirectPath="../gi2/video1"
                    analyticsFacingType="gi2"
                  />
                ),
                children: [{ path: ":questionKey", element: <GIQuestionnaire /> }],
              },
              { path: "gi2/video1", element: <GI2VideoPage1 /> },
            ]
          },
          { 
            path: "demo",
            children: [
              {
                path: "patient",
                children: [
                  {path: "landing", element: <DemoPatientLanding />},
                  {path: "choose-avatar", element: <DemoChooseAvatar />},
                  {path: "video", element: <DemoPatientVideo />},
                  {path: "shuttle", element: <DemoPatientShuttle />},
                ]
              }
            ] 
          }
        ]
      },
      // ---- Hospital Specific Login ----
      {
        element: (
          <>
              <Outlet/>
            {/* </HospitalLayout> */}
          </>
        ),
        children: [
          { path: "forgot-password", element: <ForgotPassword /> },
          { path: "reset-password", element: <ResetPassword /> },
          { path: "Login/:hospitalName", element: <Login /> },
        ]
      },
      // ---- After Login/Authentication ----
      {
        element: (
          <>
            <LogoutOnAuthTokenExpiration />
            <Outlet />
          </>
        ),
        children: [
          { 
            index: true, 
            element: <>
              <Navigate to="/Panel" />
            </> 
          },
          {
            element: <ScrollToTop />,
            children: [
              { 
                index: true, 
                element: <Navigate to="/Panel" /> 
              },
              {
                path: "Panel",
                element: <Panel />,
                errorElement: <ErrorElement />,
              },
              { path: "Admin", element: <AdminGuard><AdminLayout><AdminPage /></AdminLayout></AdminGuard>, errorElement: <ErrorElement /> },
              { path: "admin/facility/:id", element: <AdminGuard><AdminLayout><FacilityDetailPage /></AdminLayout></AdminGuard>, errorElement: <ErrorElement /> },
              { path: "admin/import", element: <AdminGuard><AdminLayout><CSVUploadPage /></AdminLayout></AdminGuard>, errorElement: <ErrorElement /> },
              { path: "admin/statistics", element: <AdminGuard><AdminLayout><AdminStatisticsPage /></AdminLayout></AdminGuard>, errorElement: <ErrorElement /> },
              { path: "admin/video-test", element: <AdminGuard><AdminLayout><AdminVideoTestPage /></AdminLayout></AdminGuard>, errorElement: <ErrorElement /> },
              { path: "admin/doctors", element: <AdminGuard><AdminLayout><AdminDoctorsPage /></AdminLayout></AdminGuard>, errorElement: <ErrorElement /> },
              { path: "admin/laxatives", element: <AdminGuard><AdminLayout><AdminLaxativesPage /></AdminLayout></AdminGuard>, errorElement: <ErrorElement /> },
              // { path: "Gister", element: <GisterFlowWrapper /> },
              { path: "Gister", element: <Gister/> },
              { 
                path: "CharacterSelection", 
                errorElement: <ErrorElement />,
              },
            ]
          }
        ]
      }
    ]
  }
  ], {
  future: {
    v7_startTransition: true,
  },
});


// Todo - Move to separate file
function LogoutOnAuthTokenExpiration() {
  const navigate = useNavigate();
  const logoutTimerRef = useRef(null);

  useEffect(() => {
    async function scheduleLogout() {
      const hospital = JSON.parse(localStorage.getItem("hospital"));
      const hospitalName = hospital?.name?.toLowerCase() || "";
      try {
        const { tokenExpTime } = (
          await axios.get("/api/auth/token-exp-time")
        ).data;

        if (!tokenExpTime) {
          navigate(`/login/${hospitalName}`, { replace: true });
          return;
        }


        const SAFETY_BUFFER_MS = 10_000;
        const logoutDelay = Math.max(
          tokenExpTime * 1000 - Date.now() - SAFETY_BUFFER_MS,
          0
        );

        clearTimeout(logoutTimerRef.current);

        logoutTimerRef.current = setTimeout(() => {
          navigate(`/login/${hospitalName}`, { replace: true });
        }, logoutDelay);

      } catch(error) {
        console.error(error);
        
        if(!hospitalName) {
          alert("Hospital name missing");
          return;
        }
        
        if(error.response.status === HttpStatusCode.NotFound || error.response.status === HttpStatusCode.Forbidden) {
          navigate(`/login/${hospitalName}`, { replace: true });
        }
      }
    }

    scheduleLogout();

    return () => {
      clearTimeout(logoutTimerRef.current);
    };
  }, [navigate]);

  return null;
}



const App = () => {
  return (
    <>
      <ToastContainer />
      <RouterProvider router={router} />
    </>
  );
};

export default App;